/* =====================================================
   GRZLY ATHLETICS — GLOBAL THEME JS
   Loader, sticky header, scroll reveals, mobile nav,
   hero parallax, quantity steppers.
   ===================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Intro loader ---------- */
  function initLoader() {
    var loader = document.querySelector('[data-site-loader]');
    if (!loader) return;

    document.body.classList.add('is-loading');

    var hide = function () {
      loader.classList.add('is-hidden');
      document.body.classList.remove('is-loading');
    };

    if (reduceMotion) {
      hide();
      return;
    }

    var minDelay = new Promise(function (resolve) { setTimeout(resolve, 900); });
    var pageReady = new Promise(function (resolve) {
      if (document.readyState === 'complete') resolve();
      else window.addEventListener('load', resolve, { once: true });
    });

    Promise.all([minDelay, pageReady]).then(hide);
    // Safety net so the loader never traps a user
    setTimeout(hide, 3500);
  }

  /* ---------- Sticky header ---------- */
  function initHeader() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    var threshold = 24;

    var onScroll = function () {
      if (window.scrollY > threshold) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile nav drawer ---------- */
  function initMobileNav() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var drawer = document.querySelector('[data-mobile-nav]');
    var closeBtn = drawer ? drawer.querySelector('[data-menu-close]') : null;
    var scrim = drawer ? drawer.querySelector('[data-menu-scrim]') : null;
    if (!toggle || !drawer) return;

    var open = function () {
      drawer.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    var close = function () {
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (scrim) scrim.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* ---------- Scroll reveal (fade-up / stagger) ---------- */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    var enabled = document.body.getAttribute('data-reveals') !== 'false';

    if (reduceMotion || !enabled || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var groups = {};
    items.forEach(function (el) {
      var group = el.getAttribute('data-reveal-group') || 'default';
      groups[group] = groups[group] || [];
      groups[group].push(el);
    });

    Object.keys(groups).forEach(function (key) {
      groups[key].forEach(function (el, i) {
        el.style.setProperty('--reveal-delay', Math.min(i * 90, 540) + 'ms');
      });
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ---------- Hero parallax ---------- */
  function initParallax() {
    if (reduceMotion) return;
    if (document.body.getAttribute('data-parallax-enabled') === 'false') return;
    var target = document.querySelector('[data-parallax]');
    if (!target) return;

    var ticking = false;
    var update = function () {
      var rect = target.getBoundingClientRect();
      var progress = 1 - (rect.top + rect.height / 2) / (window.innerHeight + rect.height);
      var offset = (progress - 0.5) * 60;
      target.style.transform = 'translateY(' + offset.toFixed(1) + 'px) scale(1.08)';
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Quantity steppers ---------- */
  function initQuantitySteppers() {
    document.querySelectorAll('[data-qty]').forEach(function (wrap) {
      var input = wrap.querySelector('input');
      var minus = wrap.querySelector('[data-qty-minus]');
      var plus = wrap.querySelector('[data-qty-plus]');
      if (!input) return;
      var clamp = function (v) {
        var min = parseInt(input.min || '1', 10);
        return Math.max(min, v);
      };
      if (minus) minus.addEventListener('click', function () {
        input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (plus) plus.addEventListener('click', function () {
        input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- Product media thumbnails ---------- */
  function initGalleries() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('[data-gallery-main] img');
      var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-full');
          if (main && src) main.src = src;
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------- Variant swatch -> disable unavailable, sync hidden select ---------- */
  function initSwatches() {
    document.querySelectorAll('[data-swatch-group]').forEach(function (group) {
      var inputs = group.querySelectorAll('input[type="radio"]');
      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          group.dispatchEvent(new CustomEvent('swatch:change', { bubbles: true, detail: { value: input.value, option: group.dataset.swatchGroup } }));
        });
      });
    });
  }

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', function () {
    initLoader();
    initHeader();
    initMobileNav();
    initReveal();
    initParallax();
    initQuantitySteppers();
    initGalleries();
    initSwatches();
  });
})();
